import styled, { css } from 'styled-components';

export const FormRoot = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
`;

export const InputRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border-radius: 8px;
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainerLow ?? '#1A1A20'};
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#46464F'};
`;

export const InputHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
`;

export const InputMeta = styled.span`
  font-size: 11px;
  font-weight: 400;
  opacity: 0.75;
`;

export const RequiredMark = styled.span`
  color: ${({ theme: { colors = {} } }) => colors.error ?? '#FF7A8A'};
  font-weight: 700;
  margin-left: 4px;
`;

export const HintText = styled.div`
  font-size: 12px;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A0A0A0'};
`;

export const ModeTabs = styled.div`
  display: flex;
  gap: 4px;
  border-radius: 6px;
  padding: 2px;
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainerLowest ?? '#0E0E14'};
  align-self: flex-start;
`;

interface TabProps {
  $active: boolean;
}

export const ModeTab = styled.button<TabProps>`
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A0A0A0'};
  ${(p) =>
    p.$active &&
    css`
      background: ${(p.theme as { colors?: Record<string, string | undefined> })
        .colors?.primary ?? '#C1C1FF'};
      color: ${(p.theme as { colors?: Record<string, string | undefined> })
        .colors?.onPrimary ?? '#1B1B1F'};
    `}
`;

export const Dropzone = styled.div<{ $dragOver: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 18px;
  border-radius: 6px;
  border: 1.5px dashed
    ${(p) =>
      p.$dragOver
        ? ((p.theme as { colors?: Record<string, string | undefined> }).colors
            ?.primary ?? '#C1C1FF')
        : ((p.theme as { colors?: Record<string, string | undefined> }).colors
            ?.outline ?? '#46464F')};
  background: ${(p) =>
    p.$dragOver
      ? ((p.theme as { colors?: Record<string, string | undefined> }).colors
          ?.surfaceContainerHigh ?? '#23232A')
      : 'transparent'};
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  & > input[type='file'] {
    display: none;
  }
`;

export const PickerList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 220px;
  overflow-y: auto;
`;

export const PickerRow = styled.li<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  background: ${(p) =>
    p.$selected
      ? ((p.theme as { colors?: Record<string, string | undefined> }).colors
          ?.primaryContainer ?? '#3D2A66')
      : 'transparent'};
  color: ${(p) =>
    p.$selected
      ? ((p.theme as { colors?: Record<string, string | undefined> }).colors
          ?.onPrimaryContainer ?? '#E5DAFF')
      : 'inherit'};

  &:hover {
    background: ${(p) =>
      p.$selected
        ? ((p.theme as { colors?: Record<string, string | undefined> }).colors
            ?.primaryContainer ?? '#3D2A66')
        : ((p.theme as { colors?: Record<string, string | undefined> }).colors
            ?.surfaceContainerHigh ?? '#23232A')};
  }
`;

export const TextField = styled.textarea`
  width: 100%;
  resize: vertical;
  min-height: 70px;
  padding: 8px 10px;
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainerLowest ?? '#0E0E14'};
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#46464F'};
  border-radius: 4px;
  color: inherit;
  font: inherit;
  font-size: 12px;
`;

export const ErrorText = styled.div`
  font-size: 11px;
  color: ${({ theme: { colors = {} } }) => colors.error ?? '#FF7A8A'};
`;

export const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 4px;
`;
